import { use, useCallback } from "react";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import ObjectCardChecksContext from "../../entityFields/ObjectCardChecksContext";

interface WorkflowActionToCheck {
  workflowId: string;
  action: WorkflowSignalType;
}

interface WorkflowActionChecksData {
  blockers: string[];
  warnings: string[];
  hasFailed: boolean;
  isFetching: boolean;
}

interface WorkflowActionChecksResult extends WorkflowActionChecksData {
  refetch: () => Promise<void>;
}

export interface WorkflowActionWithChecks extends WorkflowActionToCheck, WorkflowActionChecksData {}

const useWorkflowActionChecks = (actionToCheck: WorkflowActionToCheck | undefined): WorkflowActionChecksResult => {
  const cardChecks = use(ObjectCardChecksContext)?.actionChecks;

  const workflowId = actionToCheck?.workflowId ?? "";
  const action = actionToCheck?.action ?? "Approve";
  const usesCardChecks =
    actionToCheck !== undefined &&
    cardChecks?.workflowId === workflowId &&
    cardChecks.action === action &&
    !cardChecks.isFetching &&
    !cardChecks.hasFailed;

  const getChecks = useCallback(() => adminApi.getWorkflowActionChecks(workflowId, action), [action, workflowId]);

  const [checks, fetchError, { isFetching, fetch: refetch }] = useFetch(getChecks, {
    disabled: !actionToCheck || usesCardChecks,
  });

  if (fetchError) {
    logError(fetchError, "[useWorkflowActionChecks] getWorkflowActionChecks");
  }

  if (usesCardChecks) {
    return { ...cardChecks, refetch };
  }

  const loadedChecks = fetchError ? [] : (checks ?? []);

  return {
    blockers: loadedChecks.filter(({ severity }) => severity === "Blocker").map(({ message }) => message),
    warnings: loadedChecks.filter(({ severity }) => severity === "Warning").map(({ message }) => message),
    hasFailed: actionToCheck !== undefined && Boolean(fetchError),
    isFetching,
    refetch,
  };
};

export default useWorkflowActionChecks;
